"use client";

import { useQuery } from "@tanstack/react-query";
import {
  getSharedStakeHistoryAction,
  getUnstakeHistoryAction,
  getUnstakeDetailsAction,
  getPendingUnstakeInfoAction,
  getStakeDetailsAction,
  getStakeUnstakeSummaryAction,
  getUnstakeTxAction,
  getSharedStakeStatsAction,
  getClaimRewardTxAction,
  getCancelUnstakeTxAction,
} from "@/resources/actions/user.actions";
import { queryKeys } from "@/lib/query-keys";

/**
 * Query hook — shared stake history for a wallet
 */
export function useSharedStakeHistory(wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.stakeHistory(wallet),
    queryFn: async () => {
      const result = await getSharedStakeHistoryAction(wallet);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!wallet,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

/**
 * Query hook — unstake (unbonding) history for a wallet
 */
export function useUnstakeHistory(wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.unstakeHistory(wallet),
    queryFn: async () => {
      const result = await getUnstakeHistoryAction(wallet);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!wallet,
    staleTime: 0,
  });
}

/**
 * Query hook — unstake details by unstake ID
 */
export function useUnstakeDetails(unstakeId: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.unstakeDetails(unstakeId),
    queryFn: async () => {
      const result = await getUnstakeDetailsAction(unstakeId);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!unstakeId,
    staleTime: 0,
  });
}

/**
 * Query hook — pending unstake info for a wallet
 */
export function usePendingUnstakeInfo(wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.pendingUnstakeInfo(wallet),
    queryFn: async () => {
      if (!wallet) {
        throw new Error("Wallet is required");
      }
      const result = await getPendingUnstakeInfoAction(wallet);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: false,
    staleTime: 0,
  });
}

/**
 * Query hook — stake details by stake ID
 */
export function useStakeDetails(stakeId: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.stakeDetails(stakeId),
    queryFn: async () => {
      const result = await getStakeDetailsAction(stakeId);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!stakeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Query hook — stake and unstake summary for a wallet
 */
export function useStakeUnstakeSummary(wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.summary(wallet),
    queryFn: async () => {
      const result = await getStakeUnstakeSummaryAction(wallet);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!wallet,
    staleTime: 0,
  });
}

/**
 * Query hook — shared stake stats for a wallet
 */
export function useSharedStakeStats(wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.stats(wallet),
    queryFn: async () => {
      const result = await getSharedStakeStatsAction(wallet);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!wallet,
    staleTime: 0,
  });
}

/**
 * Query hook — unstake transaction data for a given unstake ID
 */
export function useUnstakeTx(unstakeId: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.unstakeTx(unstakeId),
    queryFn: async () => {
      const result = await getUnstakeTxAction(unstakeId);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: !!unstakeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Query hook — claim reward transaction data
 */
export function useClaimRewardTx() {
  return useQuery({
    queryKey: queryKeys.sharedStaking.claimRewardTx(),
    queryFn: async () => {
      const result = await getClaimRewardTxAction();
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: false,
    staleTime: 0,
  });
}

/**
 * Query hook — unsigned cancel-unstake tx payload for the given unstake record
 */
export function useCancelUnstakeTx(unstakeId: string, wallet: string) {
  return useQuery({
    queryKey: queryKeys.sharedStaking.cancelUnstakeTx(unstakeId, wallet),
    queryFn: async () => {
      const result = await getCancelUnstakeTxAction({ unstake_id: unstakeId, wallet });
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    enabled: false,
    staleTime: 0,
  });
}
